import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import localizedFormat from "dayjs/plugin/localizedFormat";
import "dayjs/locale/sv";
import { DATE_TIME_FORMAT } from "@lib/constants/system";

dayjs.extend(advancedFormat);
dayjs.extend(localizedFormat);

/**
 * Formats a date string using dayjs.
 * @param dateString The date string to format.
 * @param format The dayjs format string (e.g., "YYYY-MM-DD HH:mm", "L LT"). Defaults to "YYYY-MM-DD HH:mm".
 * @returns The formatted date string or "Invalid Date" or "N/A".
 */
export const formatDateTime = (
  dateString: Date | string | undefined,
  format: string = DATE_TIME_FORMAT,
  fallback: string = "N/A",
): string => {
  if (!dateString) {
    return fallback;
  }
  dayjs.locale(useNuxtApp().$i18n.locale.value ?? "sv");
  const d = dayjs(dateString);
  if (d.isValid()) {
    return d.format(format);
  } else {
    // Consider returning the original string or a more specific error
    return "Invalid Date";
  }
};

/**
 * Combines a Date object and a time string (HH:mm) into a formatted date-time string.
 * @param date The Date object.
 * @param time The time string in HH:mm format.
 * @param format The output format string. Defaults to "YYYY-MM-DD HH:mm".
 * @returns The formatted date-time string or an empty string if inputs are invalid.
 */
export const combineDateAndTime = (date: Date | null, time: string | null): Date | undefined => {
  if (!date || time === null) return;
  try {
    const [hours, minutes] = time.split(":").map(Number);
    // Use dayjs to combine date and time
    const combinedDayjs = dayjs(date).hour(hours ?? 0).minute(minutes ?? 0).second(0).millisecond(0);
    // Return as Date object.
    return combinedDayjs.toDate();
  } catch (e) {
    console.error("Error combining date and time:", e);
    return;
  }
};
